import { type Metadata, type MetadataRoute } from "next";
import type { BaseProduct, Page } from "../types";
import {
  generateCategoryAlternates,
  generatePageAlternates,
  generateProductAlternates,
  type AlternateGeneratorOptions,
  type LocalizedCategory,
} from "./alternates";

export type SitemapSegment = "pages" | "categories" | "products" | "articles";
export type SitemapId =
  | `${Lowercase<SitemapSegment>}-${string}`
  | `${Lowercase<SitemapSegment>}-${string}-${string}`;

export const generateSitemapSegments = (
  locales: string[],
  segments: SitemapSegment[]
): SitemapId[] => {
  return segments.flatMap<SitemapId>((segment) =>
    locales.map((locale) => `${segment}-${locale}` as SitemapId)
  );
};

export const parseSitemapId = (id: string) => {
  const [segment, locale, country] = id.split("-");
  return { segment: segment as SitemapSegment, locale, country };
};

export interface GenerateSitemapOptions extends AlternateGeneratorOptions {
  fetchPages: (options: AlternateGeneratorOptions) => Promise<Page[]>;
  fetchProducts: (options: AlternateGeneratorOptions) => Promise<BaseProduct[]>;
  fetchCategories: (
    options: AlternateGeneratorOptions
  ) => Promise<LocalizedCategory[] | Page[]>;
  fetchArticles: (options: AlternateGeneratorOptions) => Promise<Page[]>;
  segments?: Partial<
    Record<
      SitemapSegment,
      {
        changeFrequency?: MetadataRoute.Sitemap[number]["changeFrequency"];
        priority?: MetadataRoute.Sitemap[number]["priority"];
      }
    >
  >;
}

function headAlternatesToSitemapAlternates(alternates: Metadata["alternates"]) {
  if (!alternates?.languages) return undefined;

  return Object.fromEntries(
    Object.entries(alternates.languages).map(([locale, url]) => {
      return [locale, url!.toString()] as const;
    })
  );
}

export async function generateSitemap({
  segment,
  ...options
}: {
  segment: SitemapSegment;
} & GenerateSitemapOptions): Promise<MetadataRoute.Sitemap> {
  switch (segment) {
    case "pages":
      return [
        {
          url: "/",
          priority: 1,
          changeFrequency: "daily",
        },
        ...(await generatePageSitemap(options)),
      ];
    case "categories":
      return await generateCategorySitemap(options);
    case "products":
      return await generateProductSitemap(options);
    case "articles":
      return await generateArticlesSitemap(options);
    default:
      throw new Error(`Segment ${segment} is unsupported`);
  }
}

export async function generatePageSitemap(
  options: GenerateSitemapOptions
): Promise<MetadataRoute.Sitemap> {
  const pages = await options.fetchPages(options);

  const { changeFrequency = "monthly", priority = 0.7 } =
    options.segments?.["pages"] || {};

  return pages.map((page) => {
    const alternates = generatePageAlternates({ ...options, page });
    return {
      url: options.getURL(options.locale, page.full_slug, {
        type: "page",
        page,
      }),
      changeFrequency,
      priority,
      alternates: {
        languages: headAlternatesToSitemapAlternates(alternates),
      },
    };
  });
}

export async function generateArticlesSitemap(
  options: GenerateSitemapOptions
): Promise<MetadataRoute.Sitemap> {
  const pages = await options.fetchArticles(options);

  const { changeFrequency = "monthly", priority = 0.7 } =
    options.segments?.["pages"] || {};

  return pages.map((page) => {
    const alternates = generatePageAlternates({ ...options, page });
    return {
      url: options.getURL(options.locale, page.full_slug, {
        type: "page",
        page,
      }),
      changeFrequency,
      priority,
      alternates: {
        languages: headAlternatesToSitemapAlternates(alternates),
      },
    };
  });
}

export async function generateCategorySitemap(
  options: GenerateSitemapOptions
): Promise<MetadataRoute.Sitemap> {
  const categories = await options.fetchCategories(options);

  const { changeFrequency = "weekly", priority = 0.8 } =
    options.segments?.["categories"] || {};

  return categories.map((category) => {
    if ("full_slug" in category) {
      const alternates = generatePageAlternates({ ...options, page: category });
      return {
        url: options.getURL(options.locale, category.full_slug, {
          type: "page",
          page: category,
        }),
        changeFrequency,
        priority,
        alternates: {
          languages: headAlternatesToSitemapAlternates(alternates),
        },
      };
    }

    const alternates = generateCategoryAlternates({ ...options, category });
    return {
      url: options.getURL(options.locale, category.uri, {
        type: "category",
        category,
      }),
      changeFrequency,
      priority,
      alternates: {
        languages: headAlternatesToSitemapAlternates(alternates),
      },
    };
  });
}

export async function generateProductSitemap(options: GenerateSitemapOptions) {
  const products = await options.fetchProducts(options);

  const { changeFrequency = "weekly", priority = 0.9 } =
    options.segments?.["products"] || {};

  return products.map((product) => {
    const alternates = generateProductAlternates({ ...options, product });
    return {
      url: options.getURL(options.locale, product.uri, {
        type: "product",
        product,
      }),
      changeFrequency,
      priority,
      alternates: {
        languages: headAlternatesToSitemapAlternates(alternates),
      },
    };
  });
}

export function generateSitemapResponse(
  locales: string[],
  segments: SitemapSegment[],
  baseURL: URL
) {
  const sitemapSegments = generateSitemapSegments(locales, segments);

  const sitemapLocs = sitemapSegments.map(
    (segment) => new URL(`/s/sitemap/${segment}.xml`, baseURL)
  );

  const sitemaps = sitemapLocs
    .map((sitemap) => `<sitemap><loc>${sitemap}</loc></sitemap>`)
    .join("");

  return new Response(
    `<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">${sitemaps}</sitemapindex>`,
    {
      headers: {
        "Content-Type": "application/xml",
      },
    }
  );
}
